import styled from "styled-components";
import cssObjectForFloatingButton from "./functions/cssObjectForFloatingButton";
import merge from "lodash.merge";
import { StyledFloatingButtonProps } from "../FloatingButton.types";

const TRANSITION_DURATION = 300;
const LONG_TRANSITION_DURATION = 600;
const TRANSITION_TIMING_FUNCTION = "ease-in-out";

export const MOD_BACK_TO_TOP = "back-to-top";
export const MOD_ICON_ONLY = "icon-only";
export const MOD_FILLED = "filled";
export const MOD_SCROLLED = "scrolled";

const CLASS_BACK_TO_TOP = `.FloatingButton--${MOD_BACK_TO_TOP}`;
const CLASS_ICON_ONLY = `.FloatingButton--${MOD_ICON_ONLY}`;
const CLASS_FILLED = `.FloatingButton--${MOD_FILLED}`;
const CLASS_SCROLLED = `.FloatingButton--${MOD_SCROLLED}`;

const StyledFloatingButton = styled.button<StyledFloatingButtonProps>`
	display: flex;
	align-items: center;
	justify-content: center;
	border: none;
	width: 48px;
	height: 48px;
	padding: 0;
	transition: background-color ${TRANSITION_DURATION}ms
			${TRANSITION_TIMING_FUNCTION},
		transform ${LONG_TRANSITION_DURATION}ms ${TRANSITION_TIMING_FUNCTION},
		opacity ${LONG_TRANSITION_DURATION}ms ${TRANSITION_TIMING_FUNCTION};

	&${CLASS_BACK_TO_TOP} {
		overflow: hidden;
		opacity: 0;
	}

	.Icon {
		background: none;
	}

	.Icon svg {
		transition: fill ${TRANSITION_DURATION}ms ${TRANSITION_TIMING_FUNCTION};
	}

	&:not(:disabled) {
		cursor: pointer;
		&${CLASS_BACK_TO_TOP} .Icon svg {
			animation: slingshot-down ${LONG_TRANSITION_DURATION}ms
				${TRANSITION_TIMING_FUNCTION};
		}
		&:hover {
			&${CLASS_BACK_TO_TOP} .Icon svg {
				animation: slingshot-up ${LONG_TRANSITION_DURATION}ms
					${TRANSITION_TIMING_FUNCTION};
			}
		}
	}

	${({ backToTop, iconPosition, position, theme }) => {
		const COLOR_BG_WHITE = theme.palette.hexForRole("bg_white");

		return {
			position: !backToTop && position ? position : "fixed",
			flexDirection: iconPosition === "right" ? "row" : "row-reverse",
			gap: theme.space.sm,
			borderRadius: theme.radii.lg,
			boxShadow: `${theme.shadows.shadow_200}`,
			transform: "translateY(0px)",

			[`&${CLASS_BACK_TO_TOP}`]: {
				bottom: theme.space.lg,
				transform: "translateY(100px)",
			},

			[`&${CLASS_BACK_TO_TOP + CLASS_SCROLLED}`]: {
				transform: "translateY(0px)",
				opacity: 1,
			},

			[`&${CLASS_FILLED}`]: {
				width: "auto",
				padding: `0 ${
					iconPosition === "right" ? theme.space.xs : theme.space.lg
				}
				  0 ${iconPosition === "right" ? theme.space.lg : theme.space.xs}`,
			},

			[`&${CLASS_FILLED} .Icon`]: {
				backgroundColor: COLOR_BG_WHITE,
			},

			[`&${CLASS_ICON_ONLY} .Icon svg`]: {
				fill: `${COLOR_BG_WHITE} !important`,
			},

			".Typography": {
				color: COLOR_BG_WHITE,
			},
		};
	}}

	${({ backToTop, top, right, bottom, left, zIndex, theme }) => {
		if (backToTop) {
			return {
				right: theme.space.lg,
				zIndex: 1000,
			};
		}
		return {
			top,
			right,
			bottom,
			left,
			zIndex,
		};
	}}

	${({ theme }) => {
		const palette = theme.palette;
		const COLOR_CTA_DEFAULT = palette.hexForRole("cta_default");
		const COLOR_CTA_HOVER = palette.hexForRole("cta_hover");
		const COLOR_CTA_ACTIVE = palette.hexForRole("cta_active");
		const COLOR_CTA_DISABLED = palette.hexForRole("cta_disabled");
		return merge(cssObjectForFloatingButton(COLOR_CTA_DEFAULT), {
			"&:hover": cssObjectForFloatingButton(COLOR_CTA_HOVER),
			"&:active": cssObjectForFloatingButton(COLOR_CTA_ACTIVE),
			"&:disabled": cssObjectForFloatingButton(COLOR_CTA_DISABLED),
		});
	}}
`;

export { StyledFloatingButton };
